import Stack from "../../components/Stack"
import InputLabel from "@material-ui/core/InputLabel"
import TextField from "@material-ui/core/TextField"
import Typography from "@material-ui/core/Typography"
import type { Translations } from "../hooks/use-settings-page-translations"
import { useMembershipActions } from "../hooks/use-membership-actions"
import { makeStyles } from "@material-ui/core"
import { useInviteMemberIntent } from "../hooks/use-invite-member-intent"
import { useEffect } from "react"
import { useDebounce } from "../../hooks/use-debounce"

type MembershipBasicInfoFormProps = {
  canUpdateBasicInfo: boolean
  membership: {
    id: string
    firstname: string
    lastname: string
    userEmail: string
    hasAcceptedInvitation: boolean
    hasPendingInvitation: boolean
    canUpdateDetails: boolean
  }
  translations: Translations
}

const useMembershipBasicInfoForm = (props: MembershipBasicInfoFormProps) => {
  const actions = useMembershipActions()
  const { isInviteMemberIntent, resetInviteMemberIntent } = useInviteMemberIntent()
  const { membership } = props
  const emailError =
    isInviteMemberIntent && membership.canUpdateDetails && !membership.userEmail
      ? props.translations.provideValidEmailToInvite
      : null
  const { debouncedFn: debouncedSaveFirstname } = useDebounce((value: string) => {
    if (!value || value.trim() === membership.firstname.trim()) return
    actions.updateInfo(membership.id, { firstname: value })
  }, 500)

  const { debouncedFn: debouncedSaveLastname } = useDebounce((value: string) => {
    if (value.trim() === membership.lastname.trim()) return
    actions.updateInfo(membership.id, { lastname: value })
  }, 500)

  const { debouncedFn: debouncedSaveEmail } = useDebounce((value: string) => {
    if (value.trim() === membership.userEmail.trim()) return
    actions.updateInfo(membership.id, { userEmail: value })
  }, 500)

  const handleSaveFirstname = async (e: React.FocusEvent<HTMLInputElement>) => {
    debouncedSaveFirstname(e.target.value)
  }

  const handleSaveLastname = async (e: React.FocusEvent<HTMLInputElement>) => {
    debouncedSaveLastname(e.target.value)
  }

  const handleSaveUserEmail = async (e: React.FocusEvent<HTMLInputElement>) => {
    debouncedSaveEmail(e.target.value)
  }

  // Effect to reset invite member intent after handling email error
  useEffect(() => {
    if (isInviteMemberIntent && !emailError) {
      resetInviteMemberIntent()
    }
  }, [isInviteMemberIntent, emailError, resetInviteMemberIntent])

  return {
    isEmailError: Boolean(emailError),
    emailError,
    handleSaveFirstname,
    handleSaveLastname,
    handleSaveUserEmail,
  }
}

export const MembershipBasicInfoForm = (props: MembershipBasicInfoFormProps) => {
  const { isEmailError, emailError, handleSaveLastname, handleSaveFirstname, handleSaveUserEmail } =
    useMembershipBasicInfoForm(props)
  const { canUpdateBasicInfo, membership, translations } = props
  const shouldShowEmailField = !membership.hasAcceptedInvitation && !membership.hasPendingInvitation
  const classes = useStyles()

  return (
    <>
      <Typography variant="h6" style={{ marginBottom: 32 }}>
        {translations.basicInfo}
      </Typography>
      <Stack spacing={3}>
        <div>
          <InputLabel htmlFor="firstname">{translations.firstnameLabel}</InputLabel>
          <TextField
            id="firstname"
            name="firstname"
            defaultValue={membership.firstname}
            onChange={handleSaveFirstname}
            disabled={!canUpdateBasicInfo}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <InputLabel htmlFor="lastname">{translations.lastnameLabel}</InputLabel>
          <TextField
            id="lastname"
            name="lastname"
            defaultValue={membership.lastname}
            onChange={handleSaveLastname}
            disabled={!canUpdateBasicInfo}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <InputLabel error={isEmailError} htmlFor="userEmail">
            {translations.emailLabel}
          </InputLabel>
          <TextField
            id="userEmail"
            name="userEmail"
            defaultValue={membership.userEmail}
            onChange={handleSaveUserEmail}
            disabled={!canUpdateBasicInfo || !shouldShowEmailField}
            error={isEmailError}
            helperText={emailError || ""}
            variant="outlined"
            autoFocus={isEmailError}
            className={classes.emailField}
            fullWidth
          />
        </div>
      </Stack>
    </>
  )
}

const useStyles = makeStyles((theme) => {
  return {
    emailField: {
      "& .Mui-disabled": {
        color: theme.palette.text.primary,
      },
    },
  }
})
